import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import {
  DeliveryHistoryReservationItem,
  DeliveryResultTab,
  MailDeliverySendResultsResult,
} from '@/models/mail-delivery/mail-delivery-history'
import { StateToggleTab } from '@/components/molecules/settings/state-toggle-tab'
import { LoadingContent } from '@/components/molecules/loading-content'
import { listContainerStyle } from '@/constants/layout'
import { Hint } from '@/components/atoms/hint'

interface DeliverySendResultPanelProps {
  sendResults: MailDeliverySendResultsResult | null
  isLoading: boolean
}

export const DeliverySendResultPanel: React.FC<DeliverySendResultPanelProps> = ({ sendResults, isLoading }) => {
  const { t } = useTranslation()
  const [currentTab, setCurrentTab] = useState<DeliveryResultTab>(DeliveryResultTab.failed)

  const displayResults = currentTab === DeliveryResultTab.success ? sendResults?.succeededReservations : sendResults?.failedReservations

  return (
    <>
      <LoadingContent isLoading={isLoading} />
      <div css={{ display: 'flex', alignItems: 'center' }}>
        <StateToggleTab
          tabs={[
            { name: t('Failed deliveries'), value: DeliveryResultTab.failed },
            { name: t('Succeeded deliveries'), value: DeliveryResultTab.success },
          ]}
          currentTab={currentTab}
          onChange={setCurrentTab}
        />
        <Hint customCss={hintStyle}>
          <div className="inner-block">{t('DeliveryProcessingNote')}</div>
        </Hint>
      </div>
      <div css={[listContainerStyle, deliveryContainerStyle]}>
        {!isLoading &&
          (displayResults?.length ? (
            <DeliveryResultList results={displayResults} />
          ) : (
            <div css={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <h1>{t('No data available')}</h1>
            </div>
          ))}
      </div>
    </>
  )
}

const DeliveryResultList: React.FC<{ results: DeliveryHistoryReservationItem[] }> = ({ results }) => {
  const { t } = useTranslation()
  return (
    <table css={deliveryResultTableStyle}>
      <thead>
        <tr>
          <th>{t('Reservation ID')}</th>
          <th>{t('Guest name')}</th>
          <th>{t('Room number')}</th>
          <th>{t('To')}</th>
        </tr>
      </thead>
      <tbody>
        {results.map(result => (
          <tr className="contentRow" key={result.id}>
            <td className="reservationId">{result.reservationId}</td>
            <td className="guestName">
              <Link to={`/reservation/${result.id}?reservationId=${result.reservationId}`} css={{ color: '#f2a40b' }}>
                {result.guestName ?? '-'}
              </Link>
            </td>
            <td>{result.roomNumbers ? result.roomNumbers.join(', ') : '-'}</td>
            <td>{result.to}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const hintStyle = css({
  '.help-icon': { width: 18, height: 18, marginLeft: 12 },
  '.help-popup': {
    left: 44,
    top: -38,
    '.inner-block': {
      width: 400,
      padding: 16,
      textAlign: 'left',
      fontSize: 12,
      letterSpacing: 1.2,
      lineHeight: '20px',
      fontWeight: 'bold',
      color: '#676767',
    },
  },
})
const deliveryContainerStyle = css({
  backgroundColor: '#fff',
  padding: 24,
  marginBottom: 20,
  maxHeight: 250,
  overflow: 'auto',
  width: '100%',
})

const deliveryResultTableStyle = css({
  borderCollapse: 'collapse',
  minWidth: '100%',
  '.contentRow': {
    '&:hover': {
      background: '#FAFAFA',
    },
  },
  th: {
    textAlign: 'left',
    padding: '8px 12px',
    backgroundColor: '#F2F2F2',
    fontSize: 12,
    color: '#676767',
  },
  td: {
    padding: '8px 12px',
    borderBottom: '1px solid #E0E0E0',
    fontSize: 12,
    color: '#272727',
    height: 48,
    minWidth: 100,
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    '&.reservationId': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: 100,
    },
  },
})
